/** Version-conflict sheet — SPEC-2 §7. */

import { VersionConflictError } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { Sheet } from "@/components/ui/Sheet";

/** Field names a person would recognise; anything else shows its raw key. */
const FIELD_LABEL: Record<string, string> = {
  title: "Заголовок",
  summary: "Краткое описание",
  description_md: "Описание",
  body_md: "Текст",
  completion_note_md: "Итог",
  status_id: "Статус",
  due_at: "Срок",
  tags: "Теги",
  outcome: "Исход",
  // Spec 025: the text of a case.
  audience: "Для кого",
  problem_md: "Задача клиента",
  solution_md: "Решение",
  how_md: "Как работает",
  outcome_md: "Что получает клиент",
  demand_md: "Откуда спрос",
  demo_url: "Ссылка на демо",
};

function render(value: unknown): string {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) return value.join(", ") || "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * What the person sees when someone changed the record underneath them.
 *
 * There is deliberately **no "retry" button**. The two blind moves are the two
 * wrong ones: re-sending the stale `version` fails again, and re-sending
 * without it overwrites a colleague's work silently. So the sheet offers only
 * "перечитать" (take what is on the server and start from it) and "отменить"
 * (leave theirs alone) — a merge is a judgement, and the interface should not
 * pretend it can make it.
 *
 * Nothing was written when this appears; that sentence is on the sheet,
 * because "did my edit half-land?" is the first thing anyone wonders.
 */
export function ConflictSheet({
  conflict,
  attempted,
  onReload,
  onCancel,
}: {
  conflict: VersionConflictError | null;
  /** What this client tried to write, so the sheet can show both sides. */
  attempted?: Record<string, unknown>;
  onReload: () => void;
  onCancel: () => void;
}) {
  if (!conflict) return null;

  const keys = Object.keys(attempted ?? {}).filter(
    (key) => key !== "version" && render(attempted?.[key]) !== render(conflict.current[key]),
  );

  return (
    <Sheet open onClose={onCancel} title="Изменилось, пока ты смотрел">
      <div className="flex flex-col gap-3">
        <p className="text-cell text-text">
          Кто-то поменял запись, пока она была открыта. <b>Ничего не записано.</b>
        </p>
        <p className="text-meta text-hint">
          Ты читал версию {conflict.expected}, на сервере уже {conflict.actual}.
        </p>

        {keys.length > 0 && (
          <div className="flex flex-col gap-2">
            {keys.map((key) => (
              <div key={key} className="rounded-card border border-sep p-3">
                <p className="mb-1 text-micro uppercase tracking-[.06em] text-hint">
                  {FIELD_LABEL[key] ?? key}
                </p>
                <p className="text-meta text-hint">
                  у них: <span className="text-text">{render(conflict.current[key])}</span>
                </p>
                <p className="text-meta text-hint">
                  у тебя: <span className="text-text">{render(attempted?.[key])}</span>
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        <Button variant="strong" block onClick={onReload}>
          Перечитать
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Отменить
        </Button>
      </div>
    </Sheet>
  );
}
